import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import LogicAppsCalc from '../components/calculators/LogicAppsCalc'
import { Breadcrumbs } from '../components/shared/Breadcrumbs'
import { CalculatorContentWrapper } from '../components/shared/CalculatorContentWrapper'
import { parseURLToLogicApps } from '../utils/urlState'
import { useCalculatorStore } from '../store/calculatorStore'
import { generateBreadcrumbList } from '../utils/schemaHelpers'

export default function LogicAppsPage() {
  const setLogicAppsInputs = useCalculatorStore(s => s.setLogicAppsInputs)

  useEffect(() => {
    const urlInputs = parseURLToLogicApps()
    if (urlInputs) {
      setLogicAppsInputs(urlInputs as any)
    }
  }, [setLogicAppsInputs])
  return (
    <>
      <Helmet>
        <title>Azure Logic Apps Cost Calculator UK | AzureCalc.uk</title>
        <meta name="description" content="Calculate Azure Logic Apps costs for UK South. Compare Consumption (pay per action) vs Standard plans. Updated nightly from the Azure Retail Prices API." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/logic-apps" />
        <meta property="og:title" content="Azure Logic Apps Cost Calculator UK" />
        <meta property="og:description" content="Free independent Azure Logic Apps pricing for UK South. No sign-in required." />
        <meta property="og:url" content="https://www.azure-calc.co.uk/logic-apps" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Azure Logic Apps Cost Calculator",
          "description": "Independent Azure Logic Apps pricing calculator for UK South",
          "url": "https://www.azure-calc.co.uk/logic-apps",
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "GBP"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify(generateBreadcrumbList([{ name: 'Home', url: '/', position: 1 }, { name: 'Logic Apps Calculator', url: '/logic-apps', position: 2 }]))}</script>
      </Helmet>
      <div className="px-6 py-6 w-full">
        <div className="pb-6 mb-8" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
          <Breadcrumbs />
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            Azure Logic Apps Cost Calculator
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
            UK South pricing · Consumption and Standard plans · First 4,000 actions/month free
          </p>
        </div>
        <LogicAppsCalc />
        <CalculatorContentWrapper
          eyebrow="Logic Apps Pricing Guide"
          title="Understanding Azure Logic Apps Costs"
          intro={(
            <>
              <p className="mb-3">
                Azure Logic Apps offers two pricing models: Consumption (pay-per-action) and Standard (fixed monthly cost).
                The Consumption plan is ideal for sporadic workflows, while Standard provides predictable costs for high-volume scenarios.
              </p>
              <p>
                Current UK South pricing as of March 2026. All prices exclude VAT and are subject to Microsoft's terms.
              </p>
            </>
          )}
          sections={[
            {
              id: 'plan-comparison',
              title: 'Consumption vs Standard Plan Comparison',
              body: (
                <>
                  <div className="grid md:grid-cols-2 gap-6 mb-4">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--surface-container-low)' }}>
                      <h4 className="font-semibold mb-2 text-[#0078d4]">Consumption Plan</h4>
                      <ul className="space-y-1 text-sm">
                        <li>• <strong>£0.0001</strong> per Standard connector action</li>
                        <li>• <strong>£0.0007</strong> per Enterprise connector action</li>
                        <li>• <strong>£0.0887</strong> per GB data retention/month</li>
                        <li>• First 4,000 actions free per month</li>
                        <li>• Pay only for what you use</li>
                        <li>• No minimum commitment</li>
                      </ul>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--surface-container-low)' }}>
                      <h4 className="font-semibold mb-2 text-[#0078d4]">Standard Plan</h4>
                      <ul className="space-y-1 text-sm">
                        <li>• <strong>£738.85</strong> per month (base unit)</li>
                        <li>• <strong>£0.0109</strong> per GiB memory/hour</li>
                        <li>• <strong>£0.1528</strong> per vCPU/hour</li>
                        <li>• Includes 500,000 actions free</li>
                        <li>• Predictable monthly costs</li>
                        <li>• Advanced features and connectors</li>
                      </ul>
                    </div>
                  </div>
                  <p className="text-sm">
                    <strong>Recommendation:</strong> Use Consumption for workflows running less than 100,000 actions/month.
                    Switch to Standard when you exceed this threshold for better cost predictability.
                  </p>
                </>
              ),
            },
            {
              id: 'action-counting',
              title: 'How Actions Are Counted',
              body: (
                <>
                  <p className="mb-3">
                    Understanding action counting is crucial for accurate cost estimation. Each step in your Logic App workflow counts as a separate action execution.
                  </p>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg mb-4">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                      ⚠️ Common Misunderstanding
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      Every trigger check, action step, retry attempt, and loop iteration counts as a separate execution.
                      A workflow with 5 actions that retries 3 times = 20 billable executions (5 × 4).
                    </p>
                  </div>
                  <h4 className="font-semibold mb-2">What Counts as an Action:</h4>
                  <ul className="space-y-1 text-sm ml-4">
                    <li>• Trigger polling/checking</li>
                    <li>• Each action in your workflow</li>
                    <li>• Retry attempts (each counts separately)</li>
                    <li>• Loop iterations (each iteration counts)</li>
                    <li>• Condition evaluations</li>
                    <li>• Error handling steps</li>
                  </ul>
                </>
              ),
            },
            {
              id: 'connector-costs',
              title: 'Connector Pricing Deep Dive',
              body: (
                <>
                  <p className="mb-3">
                    Connector costs can significantly impact your Logic Apps bill. Standard connectors are cheaper but have fewer premium features.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ backgroundColor: 'var(--surface-container-low)' }}>
                          <th className="p-2 text-left font-semibold">Connector Type</th>
                          <th className="p-2 text-left font-semibold">Cost per Action</th>
                          <th className="p-2 text-left font-semibold">Examples</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-2 border-b" style={{ borderColor: 'var(--outline-variant)' }}>Built-in</td>
                          <td className="p-2 border-b" style={{ borderColor: 'var(--outline-variant)' }}>FREE</td>
                          <td className="p-2 border-b" style={{ borderColor: 'var(--outline-variant)' }}>HTTP, JSON, Variables</td>
                        </tr>
                        <tr>
                          <td className="p-2 border-b" style={{ borderColor: 'var(--outline-variant)' }}>Standard</td>
                          <td className="p-2 border-b" style={{ borderColor: 'var(--outline-variant)' }}>£0.0001</td>
                          <td className="p-2 border-b" style={{ borderColor: 'var(--outline-variant)' }}>Office 365, SharePoint, SQL</td>
                        </tr>
                        <tr>
                          <td className="p-2">Enterprise</td>
                          <td className="p-2">£0.0007</td>
                          <td className="p-2">SAP, IBM MQ, Service Bus Premium</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm mt-3">
                    <strong>Cost Optimization Tip:</strong> Review your connector usage monthly. Consider switching to built-in actions where possible,
                    or evaluate if Enterprise connectors provide enough value for their 7x cost premium.
                  </p>
                </>
              ),
            },
            {
              id: 'worked-example',
              title: 'Worked Cost Example',
              body: (
                <>
                  <p className="mb-3">
                    Let's calculate the monthly cost for a typical e-commerce order processing workflow.
                  </p>
                  <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'var(--surface-container-low)' }}>
                    <h4 className="font-semibold mb-2">Scenario: Daily Order Processing</h4>
                    <ul className="space-y-1 text-sm mb-3">
                      <li>• 50 orders per day (1,500/month)</li>
                      <li>• 8 actions per workflow (including retries)</li>
                      <li>• 6 Standard connector actions per workflow</li>
                      <li>• 2 Enterprise connector actions per workflow</li>
                      <li>• 100MB data retention per month</li>
                    </ul>
                    <div className="border-t pt-3" style={{ borderColor: 'var(--outline-variant)' }}>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="font-medium">Total Actions:</p>
                          <p>1,500 orders × 8 actions = 12,000</p>
                        </div>
                        <div>
                          <p className="font-medium">Standard Connectors:</p>
                          <p>1,500 × 6 × £0.0001 = £0.90</p>
                        </div>
                        <div>
                          <p className="font-medium">Enterprise Connectors:</p>
                          <p>1,500 × 2 × £0.0007 = £2.10</p>
                        </div>
                        <div>
                          <p className="font-medium">Data Retention:</p>
                          <p>0.1 × £0.0887 = £0.01</p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--outline-variant)' }}>
                        <p className="font-semibold text-[#0078d4]">Total Monthly Cost: £3.01</p>
                        <p className="text-xs text-green-600">✓ First 4,000 actions free</p>
                      </div>
                    </div>
                  </div>
                </>
              ),
            },
            {
              id: 'cost-pitfalls',
              title: 'Common Cost Surprises & Optimization',
              body: (
                <>
                  <h4 className="font-semibold mb-2">Hidden Cost Drivers:</h4>
                  <ul className="space-y-2 text-sm mb-4">
                    <li>
                      <strong>🔄 Retry Logic:</strong> Failed actions that retry 3 times = 4 billable executions per original action
                    </li>
                    <li>
                      <strong>🔁 Loops & Conditions:</strong> Each iteration and condition evaluation counts separately
                    </li>
                    <li>
                      <strong>📊 Polling Triggers:</strong> HTTP polling every 3 minutes = 480 executions per day, even with no data
                    </li>
                    <li>
                      <strong>💾 Data Retention:</strong> £0.09/GB/month adds up quickly with large payloads
                    </li>
                  </ul>
                  <h4 className="font-semibold mb-2">Optimization Strategies:</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-[#0078d4] mb-1">Immediate Actions</h5>
                      <ul className="text-sm space-y-1">
                        <li>• Use webhook triggers instead of polling</li>
                        <li>• Implement proper error handling to reduce retries</li>
                        <li>• Batch operations to reduce action count</li>
                        <li>• Archive old run history regularly</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium text-[#0078d4] mb-1">Long-term Planning</h5>
                      <ul className="text-sm space-y-1">
                        <li>• Monitor usage patterns monthly</li>
                        <li>• Consider Standard plan for high-volume workflows</li>
                        <li>• Use built-in connectors where possible</li>
                        <li>• Implement cost alerts and budgets</li>
                      </ul>
                    </div>
                  </div>
                </>
              ),
            },
          ]}
          methodologyNote={
            <div>
              <p className="mb-2">
                <strong>Pricing Data:</strong> Sourced from Azure Retail Prices API for UK South region in GBP.
                Updated nightly to reflect current Microsoft pricing.
              </p>
              <p className="mb-2">
                <strong>Action Counting:</strong> Based on Microsoft's official documentation.
                Each trigger check, action execution, retry, and loop iteration counts as a separate billable action.
              </p>
              <p>
                <strong>Free Tier:</strong> First 4,000 actions per month are free in Consumption plan.
                Standard plan includes 500,000 free actions per unit.
              </p>
            </div>
          }
          relatedLinks={[
            { label: 'Azure Functions Calculator', to: '/functions' },
            { label: 'App Service Calculator', to: '/app-service' },
            { label: 'Blob Storage Calculator', to: '/blob-storage' },
            { label: 'Methodology', to: '/methodology' },
          ]}
        />
      </div>
    </>
  )
}
